import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  Divider,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  DateField,
  Datagrid,
  FilterButton,
  FunctionField,
  ListContextProvider,
  RefreshButton,
  SelectInput,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";
import { FilterContext, FilterForm } from "ra-ui-materialui";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { Empty } from "../../../components/common/Empty";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Pagination } from "../../../components/Pagination";
import { QuickFilter } from "../../../components/common/QuickFilter";
import { SearchField } from "../../../components/common/SearchField";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";
import { WorkOrderState } from "../../../utils/constants";
import { normalizeValue, truncateText } from "../../../utils/utils";

const workOrderStateChoices = Object.values(WorkOrderState).map((state) => ({
  id: state,
  name: state.replace(/_/g, " "),
}));

const useStyles = makeStyles((theme) => ({
  listRoot: {
    transition: theme.transitions.create(["all"], {
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const useToolbarStyles = makeStyles(
  (theme) => ({
    toolbar: {
      justifyContent: "space-between",
      alignItems: "flex-start",
      paddingRight: 0,
      [theme.breakpoints.up("xs")]: {
        paddingLeft: 0,
      },
      [theme.breakpoints.down("xs")]: {
        paddingLeft: theme.spacing(2),
      },
    },
    leftControlsXs: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        paddingRight: theme.spacing(2),
        "& [class*='RaFilterForm-form']": {
          width: "100%",
        },
        "& .filter-field": {
          width: "100%",
          flexBasis: "100%",
          flexGrow: 1,
        },
        "& .filter-field > div:last-child": {
          display: "none",
        },
      },
    },
  }),
  { name: "WorkOrderListToolbar" },
);

const tabs = [
  {
    id: WorkOrderState.READY,
    name: "Ready",
    sortOrder: "DESC",
    showSmall: true,
  },
  {
    id: WorkOrderState.IN_PROGRESS,
    name: "In Progress",
    sortOrder: "DESC",
    showSmall: true,
  },
  {
    id: WorkOrderState.PENDING_REVIEW,
    name: "Review",
    sortOrder: "DESC",
    showSmall: true,
  },
  {
    id: WorkOrderState.COMPLETED,
    name: "Completed",
    sortOrder: "DESC",
    showSmall: false,
  },
  {
    id: WorkOrderState.CANCELLED,
    name: "Cancelled",
    sortOrder: "DESC",
    showSmall: false,
  },
];

const WorkOrderSearchField = (props) => {
  const { label, alwaysOn } = props;
  const { displayedFilters, filterValues, setFilters } = useListContext(props);

  const handleSearch = (value) => {
    if (value) {
      const normalizedValue = normalizeValue(value);
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { accountName: { matchPhrasePrefix: `${value}` } },
            { assignedTo: { matchPhrasePrefix: `${value}` } },
            { normalizedPoNumber: { matchPhrasePrefix: `${normalizedValue}` } },
            { normalizedPoNumber: { wildcard: `*${normalizedValue}*` } },
          ],
        },
        displayedFilters,
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = () => [
  <WorkOrderSearchField label="Search" alwaysOn />,
  <SelectInput
    source="state.eq"
    label="State"
    variant="outlined"
    resettable
    allowEmpty
    choices={workOrderStateChoices}
    helperText={false}
  />,
  <QuickFilter
    source="hasException"
    label="Has Exception"
    defaultValue={{ eq: true }}
  />,
];

const WorkOrderListToolbar = (props) => {
  const { filters, actions, xsDown, ...rest } = props;
  const classes = useToolbarStyles(props);

  return Array.isArray(filters) ? (
    <FilterContext.Provider value={filters}>
      <Toolbar className={classes.toolbar}>
        <Box
          display="flex"
          alignItems="flex-start"
          flexWrap="wrap"
          flexDirection={xsDown ? "column" : "row"}
          width={xsDown ? "100%" : undefined}
          className={xsDown ? classes.leftControlsXs : undefined}
        >
          <FilterForm />
        </Box>
        <span />
        {actions &&
          React.cloneElement(actions, {
            ...rest,
            filters,
            ...actions.props,
          })}
      </Toolbar>
    </FilterContext.Provider>
  ) : null;
};

const WorkOrderListActions = ({ xsDown }) =>
  !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <RefreshButton />
    </TopToolbar>
  ) : null;

const getSortOrder = (tabIndex) => tabs[tabIndex].sortOrder;

const getSortBy = () => `{"key":"workOrderByState", "search":"createDateTime"}`;

const getDefaultFilter = (tabIndex) => ({
  reloadYardId: window.appUser.reloadYardId,
  state: { eq: tabs[tabIndex].id },
});

const getDefaultSearchFilter = (tabIndex) => ({
  reloadYardId: { eq: window.appUser?.reloadYardId },
  state: { eq: tabs[tabIndex].id },
});

const getSessionTab = () => {
  const sessionTab = Number(sessionStorage.getItem("WorkOrderTab"));
  if (sessionTab && tabs[sessionTab]) return sessionTab;
  return 0;
};

export const WorkOrderList = (props) => {
  const classes = useStyles();
  const smDown = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [tabIndex, setTabIndex] = useState(getSessionTab());
  const [defaultFilter, setDefaultFilter] = useState(() =>
    getDefaultFilter(tabIndex),
  );
  const [defaultSearchFilter, setDefaultSearchFilter] = useState(() =>
    getDefaultSearchFilter(tabIndex),
  );
  const defaultSort = {
    field: getSortBy(),
    order: getSortOrder(tabIndex),
  };
  const sessionPerPage = Number(
    localStorage.getItem(`${props.resource}_perPage`),
  );
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? sessionPerPage : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Work Orders",
    defaultFilter,
    defaultSearchFilter,
    defaultPerPage,
    defaultSort,
    resource: "workOrders",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage, props.resource]);

  useEffect(() => {
    if (listContext?.filter) {
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.filter]);

  const applyScopedDefaults = (nextTabIndex) => {
    setDefaultFilter(getDefaultFilter(nextTabIndex));
    setDefaultSearchFilter(getDefaultSearchFilter(nextTabIndex));
    listContext.setSort(getSortBy(), getSortOrder(nextTabIndex));
    listContext.setPage(1);
    listContext.setFilters(
      { ...(listContext.filterValues || {}) },
      listContext.displayedFilters,
    );
  };

  const handleTabChange = (value) => {
    setTabIndex(value);
    applyScopedDefaults(value);
  };

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Work Orders" />} />
      <WorkOrderListToolbar
        {...props}
        filters={getListFilters()}
        actions={<WorkOrderListActions xsDown={xsDown} />}
        xsDown={xsDown}
      />
      <Card className={classes.listRoot}>
        <WorkOrderTabs
          {...props}
          {...listContext}
          tabValue={tabIndex}
          setTabValue={handleTabChange}
          xsDown={xsDown}
          smDown={smDown}
        />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const WorkOrderTabs = ({ tabValue, setTabValue, ...props }) => {
  const { smDown } = props;

  const handleChange = (event, value) => {
    sessionStorage.setItem("WorkOrderTab", value);
    setTabValue(value);
  };

  return (
    <>
      <Tabs
        value={tabValue}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleChange}
      >
        {tabs.map(
          (tab, index) =>
            (!smDown || tab.showSmall) && (
              <Tab
                value={index}
                key={tab.id}
                label={tab.name}
                id={`work-order-${index}`}
              />
            ),
        )}
      </Tabs>
      <Divider />
      {tabs.map(
        (tab) =>
          tab.id === tabs[tabValue].id && (
            <WorkOrderGrid {...props} key={tab.id} tabValue={tab.id} />
          ),
      )}
    </>
  );
};

const WorkOrderGrid = ({ ids, page, ...props }) => {
  const { xsDown, smDown } = props;

  if (ids.length === 0 && page === 1) {
    return (
      <Box textAlign="center" m={1}>
        <Typography variant="body1" gutterBottom>
          No work orders found
        </Typography>{" "}
      </Box>
    );
  }

  return xsDown ? (
    <SimpleList
      primaryText={(record) =>
        record.account?.displayName ?? record.accountName
      }
      secondaryText={(record) => `PO Number: ${record.poNumber}`}
      tertiaryText={(record) =>
        record.assignedTo
          ? truncateText(record.assignedTo, 12)
          : record.stepName
      }
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType={false}
    />
  ) : (
    <Datagrid rowClick={false} optimized={smDown}>
      <FunctionField
        source="accountName"
        label="Account"
        sortable
        render={(record) => accountNameRenderer(record.account || record)}
      />
      <TextField source="poNumber" label="PO Number" sortable />
      <TextField source="state" label="State" sortable />
      <TextField source="stepName" label="Step" sortable />
      <TextField source="assignedTo" label="Assigned To" sortable />
      <DateField source="createDateTime" label="Created" showTime sortable />
      <TextField source="totalUOM" label="UOM" sortable />
    </Datagrid>
  );
};
